import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useNotify } from 'react-admin';
import { TransactionsReportSummary } from 'types/embedded/transactionsReportSummary';

type Props = {
  summary?: TransactionsReportSummary;
  reportPeriod?: string;
  direction: string;
};
type ReturnValue = {
  copyData: string;
  handleCopy: () => Promise<any>;
};

export const useGetSummaryCopyData = ({ summary, reportPeriod, direction }: Props): ReturnValue => {
  const translate = useTypedTranslate();
  const notify = useNotify();

  const statusesSourceMap: Record<keyof TransactionsReportSummary, string> = {
    created: translate('app.orders.summary.created'),
    prepared: translate('app.orders.summary.prepared'),
    pending: translate('app.orders.summary.pending'),
    success: translate('app.orders.summary.success'),
    failure: translate('app.orders.summary.failure'),
    cancelled: translate('app.orders.summary.cancelled'),
    refund: translate('app.orders.summary.refund'),
    chargeback: translate('app.orders.summary.chargeback'),
    expired: translate('app.orders.summary.expired'),
    waitingConfirmation: translate('app.orders.summary.waitingConfirmation'),
    executedOrderCount: translate('app.orders.summary.executedOrderCount'),
    notExecutedOrderCount: translate('app.orders.summary.notExecutedOrderCount'),
  };

  const summaryTitle = `${translate('app.orders.ordersSummaryForPeriod')}:\n`;
  const summaryPeriod = `${translate('app.orders.summary.period')}: ${reportPeriod}\n`;
  const summaryDirection = `${translate('app.table.direction')}: ${direction}\n`;
  const statusesSummary = summary
    ? (Object.entries(statusesSourceMap) as Array<[keyof TransactionsReportSummary, string]>)
        .map(
          ([source, label]) =>
            `${label}: ${summary[source] ?? translate('app.orders.summary.noValue')}`,
        )
        .join('\n') + '\n'
    : '';
  const total = `${translate('app.orders.summary.total')}: ${Number(summary?.executedOrderCount ?? 0) + Number(summary?.notExecutedOrderCount ?? 0)}\n`;
  const copyData = summaryTitle + summaryPeriod + summaryDirection + statusesSummary + total;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(copyData);
      notify(translate('app.buttons.copied'), {
        type: 'success',
        autoHideDuration: 1000,
      });
    } catch (e: unknown) {
      notify(translate('app.buttons.copyFailed'), {
        type: 'error',
        autoHideDuration: 1000,
      });
    }
  };

  return { copyData, handleCopy };
};
